import type {
  CareplanCartDataModel,
  CareplanProductDataModel,
  SubscriptionDataModel,
  SubscriptionProductDataModel,
} from "@/domains/careplan/models/types";
import { getWeightedRating } from "@/domains/careplan/utils/rating";
import { env } from "@/env";
import type { CareplanCartItem } from "@/stores";
import { formatAmount, roundToCents } from "@/utils/money";

export type CareplanOrderLine = {
  /** The live cart line id while the product is in the order, the snapshot id once removed. */
  id: string;
  variantId: string | null;
  merchandiseId: string;
  sku: string | null;
  name: string;
  shortName: string;
  image: string;
  /** 0 once the patient removed the product; the row stays so it can be added back. */
  quantity: number;
  isActive: boolean;
  /** Care plan price per unit, what Shopify charges each month. */
  unitPrice: number;
  unitPriceAsString: string;
  /** List price per unit, shown struck through next to the care plan price. */
  unitListPrice: number;
  unitListPriceAsString: string;
  /** The plan product behind the line, when it is still recommended. */
  product: CareplanProductDataModel | null;
  /** The subscription line for this product, when the patient already subscribes to it. */
  subscriptionProduct: SubscriptionProductDataModel | null;
  isSubscribed: boolean;
};

type CreateCareplanOrderModelOptions = {
  cart: NonNullable<CareplanCartDataModel>;
  /**
    The lines the cart was created with. A product the patient removed no longer exists in the
    Shopify cart, so its price and merchandise id come from this snapshot.
  */
  recommendedCartLines: CareplanCartItem[] | null;
  /** The current plan products, in page order. */
  products: CareplanProductDataModel[];
  /** The patient's subscription, if any; its lines are priced by the subscription, not the cart. */
  subscription?: SubscriptionDataModel | null;
};

/*
  Lines persisted before the unit price fields existed only carry the line total, so the unit
  price is rebuilt from it rather than trusting the field to be there.
*/
const getUnitPrices = (item: CareplanCartItem) => {
  const unitListPrice = Number.isFinite(item.unitListPrice)
    ? item.unitListPrice
    : Number(item.originalPriceAsString);

  if (Number.isFinite(item.unitPrice)) {
    return { unitPrice: item.unitPrice, unitListPrice };
  }

  const unitPrice = item.quantity > 0 ? roundToCents(item.price / item.quantity) : unitListPrice;

  return { unitPrice, unitListPrice };
};

/**
 * The patient's order as the plan page, the sticky bar and the cart all describe it: one line per
 * recommended product in plan order, one care plan price per unit, and totals that come straight
 * from the Shopify cart so every surface agrees.
 */
export const createCareplanOrderModel = ({
  cart,
  products,
  recommendedCartLines,
  subscription = null,
}: CreateCareplanOrderModelOptions) => {
  const snapshotLines = recommendedCartLines?.length ? recommendedCartLines : cart.items;

  const liveLinesByVariantId = new Map(
    cart.items.map((item) => {
      return [item.variantId, item] as const;
    }),
  );

  const productsByVariantId = new Map(
    products.flatMap((product) => {
      return product.variantId ? [[product.variantId, product] as const] : [];
    }),
  );

  const subscriptionProductsByVariantId = new Map(
    (subscription?.products ?? []).map((product) => {
      return [product.variantId, product] as const;
    }),
  );

  const snapshotsByVariantId = new Map(
    snapshotLines.map((line) => {
      return [line.variantId, line] as const;
    }),
  );

  const orderedSnapshots = [
    ...products.flatMap((product) => {
      const snapshot = product.variantId ? snapshotsByVariantId.get(product.variantId) : undefined;

      return snapshot ? [snapshot] : [];
    }),
    ...snapshotLines.filter((line) => {
      return !line.variantId || !productsByVariantId.has(line.variantId);
    }),
  ];

  const lines = orderedSnapshots.map((snapshot): CareplanOrderLine => {
    const liveLine = snapshot.variantId ? liveLinesByVariantId.get(snapshot.variantId) : undefined;
    const product = snapshot.variantId
      ? (productsByVariantId.get(snapshot.variantId) ?? null)
      : null;
    const subscriptionProduct = snapshot.variantId
      ? (subscriptionProductsByVariantId.get(snapshot.variantId) ?? null)
      : null;

    /*
      A subscribed product is charged by the subscription contract, so its price comes from
      there; everything else is priced by the Shopify cart.
    */
    const { unitListPrice, unitPrice } = subscriptionProduct
      ? { unitPrice: subscriptionProduct.finalPrice, unitListPrice: subscriptionProduct.price }
      : getUnitPrices(liveLine ?? snapshot);
    const quantity = liveLine?.quantity ?? subscriptionProduct?.quantity ?? 0;

    return {
      id: liveLine?.id ?? snapshot.id,
      variantId: snapshot.variantId ?? null,
      merchandiseId: snapshot.merchandiseId,
      sku: snapshot.sku ?? product?.sku ?? null,
      name: snapshot.name,
      shortName: snapshot.shortName,
      image: product?.images[0] ?? liveLine?.image ?? snapshot.image,
      quantity,
      isActive: quantity > 0,
      unitPrice,
      unitPriceAsString: formatAmount(unitPrice),
      unitListPrice,
      unitListPriceAsString: formatAmount(unitListPrice),
      product,
      subscriptionProduct,
      isSubscribed: !!subscriptionProduct,
    };
  });

  const activeLines = lines.filter((line) => {
    return line.isActive;
  });

  const shippingCost = Number(cart.selectedShippingOption?.cost ?? 0);

  /* Retail, before any discount: what "vs $130 retail" and the ledger subtotal show. */
  const subtotal = roundToCents(
    activeLines.reduce((total, line) => {
      return total + line.unitListPrice * line.quantity;
    }, 0),
  );

  const total = cart.cost.total.amount;

  const extraDiscounts = cart.discounts.filter((discount) => {
    return discount.code !== env.VITE_CAREPLAN_DISCOUNT_CODE;
  });

  const extraDiscountsTotal = extraDiscounts.reduce((sum, discount) => {
    return sum + discount.total;
  }, 0);

  /*
    Care plan savings: everything the patient saves against retail, less any other code (a promo
    or survey code), which the ledger lists on its own row. Shipping is taken out first so the
    savings never count a paid delivery option. Subtotal minus savings minus the extra codes plus
    shipping always equals the total.
  */
  const savings = roundToCents(
    Math.max(subtotal - (total - shippingCost) - extraDiscountsTotal, 0),
  );

  /*
    The care plan code is "amount off each product", so Shopify allocates it to the lines rather
    than the cart; a promo code that replaces it leaves the lines without a discount.
  */
  const hasCareplanCode =
    cart.discounts.some((discount) => {
      return discount.code === env.VITE_CAREPLAN_DISCOUNT_CODE;
    }) ||
    cart.items.some((item) => {
      return item.quantity > 0 && item.careplanDiscountAmount > 0;
    });

  /* A recommended product the subscription does not cover yet: the cart's "update" path. */
  const hasNewRecommendations =
    !!subscription &&
    activeLines.some((line) => {
      return !line.isSubscribed;
    });

  return {
    cartId: cart.id,
    checkoutUrl: cart.checkoutUrl,
    subscriptionStatus: subscription?.status ?? null,
    hasNewRecommendations,
    lines,
    activeLines,
    productCount: activeLines.length,
    unitCount: activeLines.reduce((count, line) => {
      return count + line.quantity;
    }, 0),
    isEmpty: activeLines.length === 0,
    subtotal,
    subtotalAsString: formatAmount(subtotal),
    savings,
    savingsAsString: formatAmount(savings),
    /** Whether the $5-a-product care plan code is on the cart; a promo code can replace it. */
    hasCareplanCode,
    total,
    totalAsString: cart.cost.total.amountAsString,
    shipping: cart.selectedShippingOption,
    extraDiscounts,
    /* Weighted over the products in the order, so removing one changes the summary rating too. */
    rating: getWeightedRating(
      activeLines.map((line) => {
        return line.product;
      }),
    ),
  };
};
